import {
  AdditiveBlending,
  BackSide,
  Color,
  Mesh,
  ShaderMaterial,
  SphereGeometry,
} from 'three';
import { Palette } from '../Palette.js';
import { GLSL_COMMON_NOISE } from '../shaders/glslCommon.js';

/**
 * The haze the world sits in: a very dim nebula on the inside of a large sphere.
 *
 * This is the "light fog" the brief asks for, in its atmospheric sense. Scene fog
 * (`FogExp2`) handles depth attenuation for objects with real z-extent, but fog
 * alone cannot make empty space look like it has *substance*. Two independently
 * drifting noise fields, one cool and one warm, do that — and because they drift
 * at unrelated rates the sky never returns to a state you have already seen.
 *
 * Intensity is kept deliberately near the floor. Anything brighter competes with
 * the mandala and, once bloom is applied, turns the background milky.
 */

export interface NebulaVeilOptions {
  readonly radius?: number;
  readonly intensity?: number;
  readonly scale?: number;
}

const VERTEX = /* glsl */ `
varying vec3 vDirection;

void main() {
  // Object-space position doubles as the view direction on a centred sphere,
  // which avoids UV seams and pole pinching entirely.
  vDirection = normalize(position);
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
}
`;

const FRAGMENT = /* glsl */ `
${GLSL_COMMON_NOISE}

uniform float uTime;
uniform float uIntensity;
uniform float uScale;
uniform vec3  uCoolColor;
uniform vec3  uWarmColor;
uniform float uBreath;

varying vec3 vDirection;

void main() {
  vec3 direction = normalize(vDirection);

  // Two fields at different scales, drifting on different axes at rates with no
  // common period, so the pattern never visibly repeats.
  vec3 coolSample = direction * uScale + vec3(0.0, uTime * 0.006, uTime * 0.0035);
  vec3 warmSample = direction * (uScale * 0.61) + vec3(uTime * -0.0042, 0.0, uTime * 0.0027);

  float cool = fbm4(coolSample);
  float warm = fbm4(warmSample);

  // Raising the noise to a power carves out empty sky; without this the whole
  // sphere glows faintly and the black of space is lost.
  cool = pow(smoothstep(0.42, 0.95, cool), 1.9);
  warm = pow(smoothstep(0.50, 0.98, warm), 2.4);

  vec3 color = uCoolColor * cool + uWarmColor * warm * 0.75;

  // Thin the veil toward the poles so it reads as a band across the sky.
  float band = 1.0 - abs(direction.y) * 0.55;
  color *= band;

  gl_FragColor = vec4(color * uIntensity * (0.85 + 0.15 * uBreath), 1.0);
}
`;

export class NebulaVeil {
  readonly object: Mesh;
  private readonly material: ShaderMaterial;
  private readonly geometry: SphereGeometry;

  constructor(options: NebulaVeilOptions = {}) {
    const radius = options.radius ?? 420;

    // A low-poly sphere is enough: all detail comes from the fragment shader, so
    // extra vertices would only cost transform work.
    this.geometry = new SphereGeometry(radius, 32, 20);

    this.material = new ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uIntensity: { value: options.intensity ?? 0.55 },
        uScale: { value: options.scale ?? 2.1 },
        uCoolColor: { value: new Color().copy(Palette.nebulaCool) },
        uWarmColor: { value: new Color().copy(Palette.nebulaWarm) },
        uBreath: { value: 0 },
      },
      vertexShader: VERTEX,
      fragmentShader: FRAGMENT,
      side: BackSide,
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
      depthTest: false,
      fog: false,
    });

    this.object = new Mesh(this.geometry, this.material);
    this.object.frustumCulled = false;
    // Drawn before everything else so it can never occlude, only underlie.
    this.object.renderOrder = -100;
  }

  update(time: number, breath: number): void {
    this.material.uniforms['uTime']!.value = time;
    this.material.uniforms['uBreath']!.value = breath;
    this.object.rotation.y = time * 0.0016;
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}
